import { Send, Upload } from "lucide-react";
import type { FC, ReactNode, Ref } from "react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

import { askQuestion, editorOf, missingText } from "../model";
import type { AskKind, KeyFact, KeyFactActionBody } from "../types";
import { FactDecision } from "./FactDecision";
import { FactEditor } from "./FactEditor";

/** The panel an ask or an edit sits in; the focused one wears the accent ring. */
export const AskBox: FC<{
  focused: boolean;
  onFocus?: () => void;
  ref?: Ref<HTMLDivElement>;
  testId?: string;
  children: ReactNode;
}> = ({ focused, onFocus, ref, testId, children }) => (
  <div
    ref={ref}
    onMouseDown={onFocus}
    onFocusCapture={onFocus}
    data-focused={focused || undefined}
    data-testid={testId}
    className={cn(
      "mt-2.5 scroll-mt-16 rounded-lg p-3 transition-colors duration-100",
      focused ? "bg-fill-1 ring-1 ring-accent-line" : "bg-fill-1"
    )}
  >
    {children}
  </div>
);

const AskTitle: FC<{ title: ReactNode; sub?: string | null }> = ({
  title,
  sub
}) => (
  <div className="mb-2.5 flex flex-col gap-0.5">
    <p className="text-base font-medium text-fg">{title}</p>
    {sub ? (
      <p className="text-sm whitespace-pre-line text-fg-3">{sub}</p>
    ) : null}
  </div>
);

export type FactAskHandlers = {
  respond: (body: KeyFactActionBody) => void;
  edit: () => void;
  skip: () => void;
  focus: () => void;
  /** Absent where Case Files can't take an upload. */
  upload?: () => void;
  /** Absent without a case chat to seed. */
  request?: () => void;
};

/**
 * The question on a row that needs the attorney: confirm a proposal, take a
 * changed value, find a missing document (Upload, Request From Client,
 * Enter Manually) or pick and enter the value. Skip stays on every one.
 */
export const FactAsk: FC<{
  fact: KeyFact;
  kind: AskKind;
  focused: boolean;
  busy: boolean;
  handlers: FactAskHandlers;
  ref?: Ref<HTMLDivElement>;
}> = ({ fact, kind, focused, busy, handlers, ref }) => {
  const box = (children: ReactNode) => (
    <AskBox
      ref={ref}
      focused={focused}
      onFocus={handlers.focus}
      testId={`fact-ask-${fact.key}`}
    >
      {children}
    </AskBox>
  );

  if (kind === "stale")
    return box(
      <FactDecision
        fact={fact}
        busy={busy}
        onRespond={handlers.respond}
        onSkip={handlers.skip}
      />
    );

  if (kind === "proposed")
    return box(
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="mr-1 text-base font-medium text-fg">
          Is this right?
        </span>
        <Button
          variant="primary"
          size="sm"
          disabled={busy}
          onClick={() => handlers.respond({ action: "confirm" })}
        >
          Confirm
        </Button>
        <Button size="sm" disabled={busy} onClick={handlers.edit}>
          Change
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="ml-auto"
          onClick={handlers.skip}
        >
          Skip
        </Button>
      </div>
    );

  if (kind === "missing") {
    const { what, lookedIn } = missingText(fact);
    return box(
      <>
        <AskTitle
          title={`Missing: ${what}`}
          sub={lookedIn ? `Looked in ${lookedIn}.` : fact.rationale}
        />
        <div className="flex flex-wrap items-center gap-1.5">
          {handlers.upload ? (
            <Button variant="primary" size="sm" onClick={handlers.upload}>
              <Upload /> Upload
            </Button>
          ) : null}
          {handlers.request ? (
            <Button size="sm" onClick={handlers.request}>
              <Send /> Request From Client
            </Button>
          ) : null}
          <Button
            variant={handlers.upload || handlers.request ? "ghost" : "primary"}
            size="sm"
            onClick={handlers.edit}
          >
            Enter Manually
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="ml-auto"
            onClick={handlers.skip}
          >
            Skip
          </Button>
        </div>
      </>
    );
  }

  const editor = editorOf(fact);
  return box(
    <>
      <AskTitle
        title={askQuestion(fact, editor)}
        sub={editor.type === "multi" ? editor.min_note : fact.rationale}
      />
      <FactEditor
        fact={fact}
        submitLabel="Confirm"
        cancelLabel="Skip"
        busy={busy}
        onSubmit={handlers.respond}
        onCancel={handlers.skip}
      />
    </>
  );
};
